import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowDown, CalendarDays, Check, Clock, Database, LoaderCircle, Redo2, Shield,
  ShieldAlert, ShieldCheck, CircleSlash, AlertTriangle,
} from "lucide-react";
import { ActionRow, CalcResult, CausalChain, DivergingChart, EvidenceTable, FindingCard, ReasoningBlock, NoteLine } from "./blocks";
import {
  blocksOf, chainOf, currentActivity, feedBlocks, figureKey, formatDuration, isLive,
  journalFromMessages, verdictOf,
} from "./journalModel";
import { toMarkdown, toProse } from "./journalExport";

// The working panel, v4: the run as ONE sequence — what it did, in the order it did it —
// with a verification card at the end, rendered from `journal` frames.
//
// THERE IS NO SECOND STRUCTURE. It carried a plan rail, collapsible step cards and three
// tabs when a run finished, so the same work was presented twice in two shapes and the
// reader had to reconcile them ("both 'in the order it happened' and tabs looks weird",
// reported 2026-09-08). The live feed was already the honest view: blocks in the order
// they arrived, each carrying its own purpose. Finishing now settles that same feed in
// place — the model upserts by block id — rather than swapping it for a different one.
// The plan still travels in the frames (the header counts its steps); it is no longer a
// competing layout.
//
// Test hooks the Playwright checks read: [data-step-card], [data-block-kind],
// [data-working-footer], [data-working-header], [data-working-feed], [data-feed-item].

function VerdictPill({ model }) {
  const verdict = verdictOf(model);
  const Icon = verdict.key === "ok" ? ShieldCheck : verdict.key === "running" ? LoaderCircle : verdict.key === "grey" ? Shield : ShieldAlert;
  return (
    <span className={`wp-pill wp-pill-${verdict.key}`} data-verdict={verdict.key}>
      <Icon size={13} className={verdict.key === "running" ? "animate-spin" : ""} aria-hidden="true" />
      {verdict.label}
    </span>
  );
}

function useElapsedSince(startedAt, running) {
  const [now, setNow] = useState(Date.now() / 1000);
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => setNow(Date.now() / 1000), 1000);
    return () => clearInterval(id);
  }, [running]);
  if (!startedAt || !running) return "";
  return formatDuration(Math.max(0, now - startedAt) * 1000);
}

// THE LOADER: what the run has done so far to get going and what it is doing this second.
// Shown for the whole live run, above the feed — the same loader the reader saw before the
// first block, kept rather than replaced by eight empty step cards (reported live: "static
// 8 steps above, what's happening at the bottom").
function StagesList({ stages = [], current = "", startedAt = null, label = "Getting started" }) {
  const past = (stages || []).filter((s) => s.text !== current).slice(-4);
  const elapsed = useElapsedSince(startedAt, true);
  return (
    <div className="wp-stages" data-working-stages="true" role="list" aria-label={label}>
      {past.map((s, i) => (
        <div key={i} className="wp-stage-row wp-stage-done" role="listitem">
          <Check size={14} aria-hidden="true" /><span>{s.text}</span>
        </div>
      ))}
      <div className="wp-stage-row wp-stage-now" role="listitem" data-working-stage="true">
        <LoaderCircle size={14} className="animate-spin" aria-hidden="true" />
        <span>{current || "Working"}<span className="wp-caret" aria-hidden="true" /></span>
        {elapsed && <span className="wp-stage-el">{elapsed}</span>}
      </div>
    </div>
  );
}

// One block of the live feed. A block that arrives after the panel opened slides in and,
// for prose, is revealed as if written; blocks already present when the panel opened
// (a reload mid-run) show at once — replaying a typewriter over old text would lie
// about what is new.
function FeedItem({ block, stream, flashKey, onBackToAnswer, decimals }) {
  let body = null;
  switch (block.kind) {
    case "reasoning": body = <ReasoningBlock block={block} stream={stream} />; break;
    case "action": body = <ActionRow block={block} />; break;
    case "evidence_table": body = <EvidenceTable block={block} flashKey={flashKey} decimals={decimals} />; break;
    case "evidence_chart": body = <DivergingChart block={block} flashKey={flashKey} decimals={decimals} />; break;
    case "calc_result": body = <CalcResult block={block} flashKey={flashKey} decimals={decimals} />; break;
    case "finding": body = <FindingCard block={block} stream={stream} onBackToAnswer={onBackToAnswer ? () => onBackToAnswer(block.step_n) : null} />; break;
    case "note": body = <NoteLine block={block} />; break;
    default: return null;
  }
  return <div className={`wp-feed-item${stream ? " wp-feed-new" : ""}`} data-feed-item={block.id}>{body}</div>;
}

function RunFeed({ model, live, flashKey = "", onBackToAnswer = null }) {
  const run = model.run || {};
  const blocks = feedBlocks(model);
  // Ids present at first render never animate; everything that lands afterwards does.
  const initialRef = useRef(null);
  if (initialRef.current === null) initialRef.current = new Set(blocks.map((b) => b.id));
  const awaiting = blocks.some((b) => b.kind === "action" && b.state === "running" && (b.verb === "queried" || b.verb === "calculated"));
  return (
    <div className="wp-feed" data-working-feed="true">
      {live && (
        <StagesList stages={run.stages} current={currentActivity(model)} startedAt={run.started_at}
                    label="What the analysis is doing" />
      )}
      {blocks.map((block) => (
        <FeedItem key={block.id} block={block} decimals={run.decimals} stream={!initialRef.current.has(block.id)}
                  flashKey={flashKey} onBackToAnswer={onBackToAnswer} />
      ))}
      {awaiting && (
        <div className="wp-skel" aria-hidden="true"><i style={{ width: "70%" }} /><i style={{ width: "85%" }} /><i style={{ width: "60%" }} /></div>
      )}
    </div>
  );
}

function scrollParentOf(node) {
  let el = node ? node.parentElement : null;
  while (el) {
    const overflow = window.getComputedStyle ? window.getComputedStyle(el).overflowY : "";
    if (overflow === "auto" || overflow === "scroll" || (el.className || "").toString().includes("overflow-y-auto")) return el;
    el = el.parentElement;
  }
  return null;
}


// ── where a cited figure lands ──────────────────────────────────────────────────
//
// A figure's text in the answer (one decimal, K/M/B) and its text in a panel cell (two
// decimals, or the exact value) seldom agree, so a cell is matched by VALUE: the exact
// value the citation carries, else any cell whose number rounds to the cited figure at
// the precision the figure was written to — the same rule the backend's grounding check
// binds figures by.
const SCALE = { K: 1e3, k: 1e3, M: 1e6, m: 1e6, mn: 1e6, MM: 1e6, B: 1e9, bn: 1e9 };

export function parseFigure(text) {
  const m = String(text || "").trim().match(/^[-−+]?\s*([\d,]*\.?\d+)\s*(K|k|M|m|mn|MM|B|bn)?\s*(%|pp|points?)?$/);
  if (!m) return null;
  const digits = m[1].replace(/,/g, "");
  const scale = m[2] ? SCALE[m[2]] : 1;
  const decimalsWritten = digits.includes(".") ? digits.split(".")[1].length : 0;
  const shift = scale === 1 ? 0 : Math.round(Math.log10(scale));
  const value = parseFloat(digits) * scale;
  if (!Number.isFinite(value)) return null;
  return { value, decimals: decimalsWritten - shift };
}

const roundTo = (value, decimals) => {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
};

export function landingCell(root, figureText, exactText) {
  if (!root) return null;
  const exactKey = figureKey(exactText);
  if (exactKey) {
    const byExact = root.querySelector(`[data-exact="${exactKey}"]`);
    if (byExact) return byExact;
  }
  const key = figureKey(figureText);
  if (key) {
    const byText = root.querySelector(`[data-figure="${key}"]`);
    if (byText) return byText;
  }
  const figure = parseFigure(figureText);
  if (!figure) return null;
  const wanted = Math.abs(roundTo(figure.value, figure.decimals));
  const cells = root.querySelectorAll("[data-figure]");
  for (const cell of cells) {
    const candidates = [cell.getAttribute("data-exact"), cell.getAttribute("title"), cell.textContent];
    for (const text of candidates) {
      const parsed = parseFigure(text);
      if (!parsed) continue;
      if (Math.abs(roundTo(parsed.value, figure.decimals)) === wanted) return cell;
    }
  }
  return null;
}

export default function WorkingPanel({ messages, anchorTarget = "", anchorFigure = "", onExport = null, onBackToAnswer = null }) {
  const model = useMemo(() => journalFromMessages(messages), [messages]);
  const live = isLive(model);
  const run = model.run || {};
  const [flashKey, setFlashKey] = useState("");
  const [scrolledUp, setScrolledUp] = useState(false);
  const rootRef = useRef(null);
  const blockCount = model.order.length;
  const elapsed = useElapsedSince(run.started_at, live);

  // Copy and Narrate read the model, not the DOM.
  useEffect(() => {
    if (onExport) onExport({ markdown: toMarkdown(model), prose: toProse(model) });
  }, [model, onExport]);

  // A CITATION lands on the figure itself. There are no step cards to open any more, so
  // the anchor highlights the figure and scrolls the block holding it into view.
  //
  // `anchorFigure` is "70.8M" or "70.8M|70,812,345.67" — the figure as the answer wrote
  // it and, after the bar, the exact value behind it. The answer writes one decimal and
  // the panel may show two, so the text rarely agrees; the VALUE does. The cell is found
  // by its exact value first, by the figure's own digits second, and last by rounding:
  // any cell that rounds to the cited figure at the figure's precision is the one.
  useEffect(() => {
    if (!anchorTarget && !anchorFigure) return undefined;
    const [figureText, exactText] = String(anchorFigure || "").split("|");
    const found = landingCell(rootRef.current, figureText, exactText);
    const key = found ? (found.getAttribute("data-figure") || "") : figureKey(figureText);
    setFlashKey(key);
    const focus = setTimeout(() => {
      const block = found && found.closest("[data-feed-item]");
      if (block && block.scrollIntoView) block.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
    const timer = setTimeout(() => setFlashKey(""), 2600);
    return () => { clearTimeout(timer); clearTimeout(focus); };
  }, [anchorTarget, anchorFigure]);

  // Live: follow the newest block unless the reader scrolled away.
  useEffect(() => {
    if (!live || !rootRef.current) return undefined;
    const parent = scrollParentOf(rootRef.current);
    if (!parent) return undefined;
    const onScroll = () => {
      const nearBottom = parent.scrollHeight - parent.scrollTop - parent.clientHeight < 120;
      setScrolledUp(!nearBottom);
    };
    parent.addEventListener("scroll", onScroll, { passive: true });
    return () => parent.removeEventListener("scroll", onScroll);
  }, [live]);

  // Live: the newest block is at the end of the loose section when one exists (blocks land
  // there first).
  useEffect(() => {
    if (!live || scrolledUp || !rootRef.current) return;
    const target = rootRef.current.querySelector("[data-working-feed]");
    if (target && target.scrollIntoView) target.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [blockCount, live, scrolledUp]);

  const jumpToLatest = () => {
    const parent = scrollParentOf(rootRef.current);
    if (parent) parent.scrollTo({ top: parent.scrollHeight, behavior: "smooth" });
    setScrolledUp(false);
  };

  const chain = chainOf(model);
  const verdict = verdictOf(model);
  // NO STEP COUNT. "5 of 6 steps ran" counted the step auditor's verdicts, and a reader
  // could not check them; what they can check is the work, which is the feed below.
  const facts = [live ? elapsed : formatDuration(run.duration_ms), run.data_through].filter(Boolean);
  const counts = (run.verification || {}).flow || {};

  if (!model.run && !model.plan.length && !blockCount) return null;

  return (
    <div className="wp" ref={rootRef} data-working-panel="v4">
      {/* THE VERDICT AND THE FACTS, nothing else. The playbook's name ("Business status
          review") and the detail toggle both went from here on a review (2026-09-10):
          the sticky header above already names the run, the skill it followed is not the
          reader's business, and a panel whose rows are one line each has no details to
          hide. The compact preference went with the button — there is no other way in. */}
      <div className="wp-head" data-working-header="true">
        <div className="wp-head-row">
          <div className="wp-meta">
            <VerdictPill model={model} />
            {facts.map((f, i) => (
              <React.Fragment key={i}><span className="wp-dot">·</span><span>{f}</span></React.Fragment>
            ))}
          </div>
        </div>
        {/* HOW THE QUESTION WAS READ. One line the backend wrote from the catalogue's display
            names — measure, source, breakdown, filters, period (`run.read_as`, correctness plan
            step 1) — under the title, so a reader checks the reading before the figures. It
            arrives in the header only when the client's `reading_shown` flag is on; nothing
            here derives a reading from the question. */}
        {run.read_as && (
          <div className="wp-readas" data-working-readas="true">
            <span className="wp-readas-label">Read as:</span> {run.read_as}
          </div>
        )}
      </div>

      <div className="wp-body">
        {/* NO QUESTION LINE HERE. The panel's sticky header is the question now (see
            StreamViewer), so repeating it two lines below said the same thing twice. */}
        {model.error && (
          <div className="wp-banner wp-banner-error" role="alert">
            <AlertTriangle size={15} aria-hidden="true" />
            <div>The analysis could not finish. Nothing shown here should be forwarded. Try again or ask a narrower question.</div>
          </div>
        )}
        {!model.error && !live && run.status === "limit_tripped" && (
          <div className="wp-banner" role="status">
            <ShieldAlert size={15} aria-hidden="true" />
            <div>This analysis stopped early, at its own limit. The figures shown were checked; the conclusion may be incomplete.</div>
          </div>
        )}
        {/* The run asked a clarifying question instead of answering: no query ran, so there
            is nothing to verify and nothing to warn about. */}
        {!model.error && !live && run.status === "asked" && (
          <div className="wp-banner" role="status">
            <Shield size={15} aria-hidden="true" />
            <div>The analysis asked a question back instead of answering — nothing was queried.</div>
          </div>
        )}
        {/* The run DECLINED (correctness plan, step 4): the draft was not about what was asked
            and was kept aside; the reader got the reading and what the data holds instead. */}
        {!model.error && !live && run.status === "declined" && (
          <div className="wp-banner" role="status" data-working-declined="true">
            <ShieldAlert size={15} aria-hidden="true" />
            <div>The analysis could not answer this reliably: what it fetched was not about the question as read. The answer gives the reading and what the data holds; no figure from the unsent draft was forwarded.</div>
          </div>
        )}
        {!model.error && !live && run.status && run.status !== "answered" && run.status !== "limit_tripped" && run.status !== "asked" && run.status !== "declined" && (
          <div className="wp-banner" role="status">
            <ShieldAlert size={15} aria-hidden="true" />
            <div>Some statements in the answer could not be fully verified against the data — treat them with care.</div>
          </div>
        )}

        {chain && <CausalChain block={chain} decimals={(model.run || {}).decimals} />}

        {(blockCount > 0 || live) && (
          <RunFeed model={model} live={live} flashKey={flashKey}
                   onBackToAnswer={onBackToAnswer} />
        )}

        {!blockCount && !live && (
          <div className="wp-hint" data-working-empty="true">Nothing to show — this answer was given without querying data. Ask a follow-up to run the analysis.</div>
        )}

        {/* The datasets this run read — a fact about the answer, not a view to switch to. */}
        {!live && (run.datasets || []).length > 0 && (
          <div className="wp-ds-strip" data-working-datasets="true">
            <Database size={14} aria-hidden="true" />
            <span>Read {(run.datasets || []).map((d) => d.label || d.name).join(", ")}</span>
            {run.data_through && <><span className="wp-dot">·</span><span>{run.data_through}</span></>}
          </div>
        )}
        {!live && run.data_caveat && (
          <div className="wp-banner wp-banner-quiet"><Clock size={15} aria-hidden="true" /><div>{run.data_caveat}.</div></div>
        )}

        {/* Verification */}
        {!live && blockCount > 0 && (run.verification || model.footer) && (
          <div className="wp-verify" data-working-footer="true">
            <div className="wp-verify-hd">
              {verdict.key === "ok" ? <ShieldCheck size={16} aria-hidden="true" /> : <ShieldAlert size={16} aria-hidden="true" />}
              {/* "Verified" ONLY for the verified label: a green key alone also covers
                  "Figures and fit checked", which is not the word Verified. */}
              {verdict.key === "ok" && (run.verification?.label ? run.verification.label === "verified" : true)
                ? `Verified in ${run.verification?.passes || 1} pass${(run.verification?.passes || 1) === 1 ? "" : "es"}`
                : verdict.label}
            </div>
            <ul>
              {run.verification?.figures_checked !== null && run.verification?.figures_checked !== undefined && (
                <li><Check size={13} aria-hidden="true" />{run.verification.figures_checked} figures matched to recorded data</li>
              )}
              {/* redrafts = drafts thrown back by a failed check (older backends send it as
                  `rederived`). It is not a count of figures, and on an unverified answer it is
                  a warning, not a tick. */}
              {(run.verification?.redrafts ?? run.verification?.rederived) > 0 && (() => {
                const n = run.verification.redrafts ?? run.verification.rederived;
                return verdict.key === "ok"
                  ? <li><Check size={13} aria-hidden="true" />Redrafted {n} time{n === 1 ? "" : "s"} before the checks passed</li>
                  : <li className="wp-li-warn"><ShieldAlert size={13} aria-hidden="true" />Redrafted {n} time{n === 1 ? "" : "s"}; the checks still did not pass</li>;
              })()}
              {run.verification?.unsourced > 0 && (
                <li className="wp-li-warn"><ShieldAlert size={13} aria-hidden="true" />{run.verification.unsourced} figure{run.verification.unsourced === 1 ? "" : "s"} could not be matched to a recorded query</li>
              )}
            </ul>
            {model.footer?.facts && (
              <div className="wp-foot">{model.footer.facts.filter((f) => !/steps? (ran|unreported)/.test(f)).join(" · ")}</div>
            )}
          </div>
        )}

      </div>

      {live && scrolledUp && (
        <button type="button" className="wp-jump" onClick={jumpToLatest}>
          <ArrowDown size={13} aria-hidden="true" />Jump to latest
        </button>
      )}
      <div className="sr-only" aria-live="polite">
        {live ? currentActivity(model) : (run.status ? verdict.label : "")}
      </div>
    </div>
  );
}
